/**
 * CSS motion for `html()`: `@keyframes` + `animation`, and `transition` with `:hover` / `:active`, read into plain data the block tools turn
 * into native clips and Properties states. Pure functions: nothing here touches a scene. Anything it cannot place goes to `warn`.
 *
 * Units: lengths stay in px / % until the unit's width is known (`bakePose`), rotation is degrees, a pose is `translate * rotate * scale`
 * about the block's centre. CSS axes map to the scene's as x right, y down -> y up, z toward the viewer; a positive CSS rotation is clockwise
 * on screen (about z) and tips the top / right edge away (about x / y).
 */
import type { TransformKeyframe } from '../../scenedata/behaviors/transformkeyframe';

type Warn = (message: string) => void;

// ── Values ──────────────────────────────────────────────

/** A length as px plus a share of the block's width (CSS `%` on a transform is of the element's own box). */
export interface Len { px: number; pct: number }
export interface Pose {
    t: [Len, Len, Len];
    /** Degrees about x, y, z (CSS sense). */
    r: [number, number, number];
    s: [number, number, number];
}
export const REST: Pose = { t: [{ px: 0, pct: 0 }, { px: 0, pct: 0 }, { px: 0, pct: 0 }], r: [0, 0, 0], s: [1, 1, 1] };
// A JSON copy: the collector also runs in QuickJS hosts, which define no structuredClone.
const restPose = (): Pose => JSON.parse(JSON.stringify(REST)) as Pose;

export type Timing = { kind: 'bezier'; p: [number, number, number, number] } | { kind: 'linear' };
const EASE_ALIAS: Record<string, [number, number, number, number]> = {
    ease: [0.25, 0.1, 0.25, 1], 'ease-in': [0.42, 0, 1, 1], 'ease-out': [0, 0, 0.58, 1], 'ease-in-out': [0.42, 0, 0.58, 1],
};
const LINEAR: Timing = { kind: 'linear' };
/** CSS's default timing function. */
const EASE: Timing = { kind: 'bezier', p: EASE_ALIAS.ease };

export interface Frame { offset: number; pose?: Pose; opacity?: number; timing?: Timing }
export interface Keyframes { name: string; frames: Frame[] }
export interface AnimationSpec {
    name: string;
    duration: number;
    delay: number;
    timing: Timing;
    iterations: number;
    direction: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
    fill: 'none' | 'forwards' | 'backwards' | 'both';
}
export interface TransitionSpec { properties: ('transform' | 'opacity')[]; duration: number; delay: number; timing: Timing }
export interface Rule { tag?: string; classes: string[]; id?: string; pseudo: '' | ':hover' | ':active'; decls: Record<string, string>; weight: number; order: number }
export interface Sheet { keyframes: Map<string, Keyframes>; rules: Rule[] }

// ── Tokens ──────────────────────────────────────────────

/** Split on a separator outside parentheses. */
function splitTop(source: string, sep: RegExp): string[] {
    const out: string[] = [];
    let depth = 0;
    let start = 0;
    for (let i = 0; i < source.length; i++) {
        const c = source[i];
        if (c === '(') depth++;
        else if (c === ')') depth = Math.max(0, depth - 1);
        else if (depth === 0 && sep.test(c)) {
            if (i > start) out.push(source.slice(start, i));
            start = i + 1;
        }
    }
    if (start < source.length) out.push(source.slice(start));
    return out.map(s => s.trim()).filter(Boolean);
}

export function parseDecls(source: string): Record<string, string> {
    const out: Record<string, string> = {};
    for (const part of splitTop(source, /;/)) {
        const at = part.indexOf(':');
        if (at < 0) continue;
        const name = part.slice(0, at).trim().toLowerCase();
        const value = part.slice(at + 1).trim().replace(/\s*!important$/i, '').toLowerCase();
        if (name) out[name] = value;
    }
    return out;
}

const seconds = (token: string): number | null => {
    const m = /^(-?\d*\.?\d+)(ms|s)$/.exec(token);
    return m ? (m[2] === 'ms' ? parseFloat(m[1]) / 1000 : parseFloat(m[1])) : null;
};

export function parseTiming(token: string, where: string, warn: Warn): Timing | null {
    if (token === 'linear') return LINEAR;
    if (EASE_ALIAS[token]) return { kind: 'bezier', p: EASE_ALIAS[token] };
    const m = /^cubic-bezier\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)$/.exec(token);
    if (m) {
        const p = [1, 2, 3, 4].map(i => parseFloat(m[i])) as [number, number, number, number];
        if (p[0] >= 0 && p[0] <= 1 && p[2] >= 0 && p[2] <= 1) return { kind: 'bezier', p };
        warn(`${where}: ${token} needs x values between 0 and 1; used linear.`);
        return LINEAR;
    }
    if (token.startsWith('steps(') || token === 'step-start' || token === 'step-end') {
        warn(`${where}: timing function ${token} is not supported; used linear.`);
        return LINEAR;
    }
    return null;
}

/** The nearest Canopy easing for a CSS timing function. */
export function canopyEasing(timing: Timing): string {
    if (timing.kind === 'linear') return 'linear';
    for (const [name, p] of Object.entries(EASE_ALIAS)) {
        if (p.every((v, i) => v === timing.p[i])) return name === 'ease-in' ? 'easeIn' : name === 'ease-out' ? 'easeOut' : name === 'ease-in-out' ? 'easeInOut' : 'cubic-bezier(0.25, 0.1, 0.25, 1)';
    }
    return `cubic-bezier(${timing.p.join(', ')})`;
}

/** Progress through a timing function: input and output are 0..1. */
export function applyTiming(timing: Timing, u: number): number {
    if (timing.kind === 'linear') return u;
    const [x1, y1, x2, y2] = timing.p;
    if (u <= 0) return 0;
    if (u >= 1) return 1;
    const at = (a: number, b: number, t: number) => 3 * a * (1 - t) * (1 - t) * t + 3 * b * (1 - t) * t * t + t * t * t;
    let lo = 0;
    let hi = 1;
    let t = u;
    for (let i = 0; i < 40; i++) {
        t = (lo + hi) / 2;
        if (at(x1, x2, t) < u) lo = t; else hi = t;
    }
    return at(y1, y2, t);
}

// ── Transform ───────────────────────────────────────────

function length(token: string): Len | null {
    const m = /^(-?\d*\.?\d+)(px|rem|em|%)?$/.exec(token.trim());
    if (!m) return null;
    const v = parseFloat(m[1]);
    if (m[2] === '%') return { px: 0, pct: v };
    if (m[2] === 'rem' || m[2] === 'em') return { px: v * 16, pct: 0 };
    if (m[2] === 'px') return { px: v, pct: 0 };
    return v === 0 ? { px: 0, pct: 0 } : null;
}
function angle(token: string): number | null {
    const m = /^(-?\d*\.?\d+)(deg|rad|turn|grad)?$/.exec(token.trim());
    if (!m) return null;
    const v = parseFloat(m[1]);
    if (m[2] === 'rad') return v * 180 / Math.PI;
    if (m[2] === 'turn') return v * 360;
    if (m[2] === 'grad') return v * 0.9;
    if (m[2] === 'deg') return v;
    return v === 0 ? 0 : null;
}
const add = (a: Len, b: Len): Len => ({ px: a.px + b.px, pct: a.pct + b.pct });

/** A `transform` value as a pose: translate, rotate and scale functions (others are reported and left out). */
export function parseTransform(value: string, where: string, warn: Warn): Pose {
    const pose = restPose();
    if (value === 'none') return pose;
    const re = /([a-zA-Z0-9]+)\(([^)]*)\)/g;
    let m: RegExpExecArray | null;
    let seen = 0;
    while ((m = re.exec(value))) {
        seen++;
        const fn = m[1].toLowerCase();
        const args = m[2].split(',').map(a => a.trim()).filter(Boolean);
        const lens = args.map(length);
        const angles = args.map(angle);
        const nums = args.map(a => (/^-?\d*\.?\d+$/.test(a) ? parseFloat(a) : NaN));
        const bad = () => warn(`${where}: transform ${fn}(${m![2]}) is not understood; ignored.`);
        if (fn === 'translate' || fn === 'translatex' || fn === 'translatey' || fn === 'translatez' || fn === 'translate3d') {
            const axes = fn === 'translatex' ? [0] : fn === 'translatey' ? [1] : fn === 'translatez' ? [2] : fn === 'translate3d' ? [0, 1, 2] : [0, 1];
            if (args.length === 0 || args.length > axes.length || lens.some(l => l === null)) { bad(); continue; }
            lens.forEach((l, k) => { pose.t[axes[k]] = add(pose.t[axes[k]], l!); });
        } else if (fn === 'rotate' || fn === 'rotatez' || fn === 'rotatex' || fn === 'rotatey') {
            if (args.length !== 1 || angles[0] === null) { bad(); continue; }
            pose.r[fn === 'rotatex' ? 0 : fn === 'rotatey' ? 1 : 2] += angles[0];
        } else if (fn === 'scale' || fn === 'scalex' || fn === 'scaley' || fn === 'scalez' || fn === 'scale3d') {
            if (args.length === 0 || nums.some(Number.isNaN)) { bad(); continue; }
            if (fn === 'scale') { pose.s[0] *= nums[0]; pose.s[1] *= nums[1] ?? nums[0]; } else if (fn === 'scale3d') {
                if (nums.length !== 3) { bad(); continue; }
                nums.forEach((n, k) => { pose.s[k] *= n; });
            } else pose.s[fn === 'scalex' ? 0 : fn === 'scaley' ? 1 : 2] *= nums[0];
        } else {
            warn(`${where}: transform function ${fn}() is not supported (translate, rotate and scale are); ignored.`);
        }
    }
    if (!seen) warn(`${where}: transform "${value}" is not understood; ignored.`);
    return pose;
}

// ── Sheet ───────────────────────────────────────────────

const stripComments = (s: string) => s.replace(/\/\*[\s\S]*?\*\//g, '');

/** Top-level `prelude { body }` blocks of a stylesheet. */
function blocks(source: string): { prelude: string; body: string }[] {
    const out: { prelude: string; body: string }[] = [];
    let i = 0;
    while (i < source.length) {
        const open = source.indexOf('{', i);
        if (open < 0) break;
        let depth = 1;
        let j = open + 1;
        while (j < source.length && depth > 0) { if (source[j] === '{') depth++; else if (source[j] === '}') depth--; j++; }
        out.push({ prelude: source.slice(i, open).trim(), body: source.slice(open + 1, depth === 0 ? j - 1 : j) });
        i = j;
    }
    return out;
}

const MOTION_PROPS = /^(animation(-[a-z-]+)?|transition(-[a-z-]+)?|transform|opacity)$/;
const SELECTOR = /^([a-z][\w-]*)?((?:[.#][\w-]+)*)(:hover|:active)?$/;

/** The declarations `html()` reads from an inline style or a stylesheet rule (motion ones are read by `motion()`). */
export const READ_CSS: readonly string[] = [
    'display', 'flex-direction', 'flex-wrap', 'flex-flow', 'gap', 'row-gap', 'column-gap', 'padding', 'border-radius', 'align-items', 'justify-content',
    'flex', 'flex-grow', 'flex-shrink', 'flex-basis', 'font-weight', 'text-align',
    'color', 'background', 'background-color', 'font-family', 'font-style', 'font-size', 'letter-spacing', 'line-height', 'text-transform',
    'grid-template-columns',
    'border', 'border-top', 'border-right', 'border-bottom', 'border-left', 'border-width', 'border-color', 'border-style', 'box-shadow', 'backdrop-filter',
    'position', 'top', 'right', 'bottom', 'left', 'inset', 'z-index', 'overflow',
];

export function parseSheet(source: string, warn: Warn): Sheet {
    const sheet: Sheet = { keyframes: new Map(), rules: [] };
    for (const { prelude, body } of blocks(stripComments(source))) {
        const kf = /^@(?:-webkit-)?keyframes\s+([\w-]+)$/i.exec(prelude);
        if (kf) { sheet.keyframes.set(kf[1], parseKeyframes(kf[1], body, warn)); continue; }
        if (prelude.startsWith('@')) { warn(`stylesheet: ${prelude.split(/\s/)[0]} is not supported; skipped.`); continue; }
        const decls = parseDecls(body);
        for (const selector of splitTop(prelude, /,/)) {
            const m = SELECTOR.exec(selector.trim().toLowerCase());
            if (!m || (!m[1] && !m[2])) { warn(`stylesheet: selector "${selector}" is not supported (a tag, .class or #id, with :hover or :active); skipped.`); continue; }
            const classes = [...m[2].matchAll(/\.([\w-]+)/g)].map(c => c[1]);
            const id = /#([\w-]+)/.exec(m[2])?.[1];
            sheet.rules.push({
                ...(m[1] ? { tag: m[1] } : {}), classes, ...(id ? { id } : {}), pseudo: (m[3] ?? '') as Rule['pseudo'], decls,
                weight: (id ? 100 : 0) + classes.length * 10 + (m[1] ? 1 : 0), order: sheet.rules.length,
            });
        }
        for (const name of Object.keys(decls)) {
            if (!MOTION_PROPS.test(name) && !READ_CSS.includes(name)) {
                warn(`stylesheet: CSS "${name}" is not supported; ignored.`);
            }
        }
    }
    return sheet;
}

function parseKeyframes(name: string, body: string, warn: Warn): Keyframes {
    const frames: Frame[] = [];
    for (const { prelude, body: inner } of blocks(body)) {
        const offsets = splitTop(prelude, /,/).map(p => p === 'from' ? 0 : p === 'to' ? 1 : /^\d*\.?\d+%$/.test(p) ? parseFloat(p) / 100 : NaN);
        if (offsets.some(o => Number.isNaN(o) || o < 0 || o > 1)) { warn(`@keyframes ${name}: "${prelude}" is not a keyframe selector; skipped.`); continue; }
        const decls = parseDecls(inner);
        const frame: Frame = { offset: 0 };
        for (const [prop, value] of Object.entries(decls)) {
            if (prop === 'transform') frame.pose = parseTransform(value, `@keyframes ${name}`, warn);
            else if (prop === 'opacity') {
                const v = parseFloat(value);
                if (Number.isFinite(v)) frame.opacity = Math.min(1, Math.max(0, v)); else warn(`@keyframes ${name}: opacity ${value} is not a number; ignored.`);
            } else if (prop === 'animation-timing-function') {
                const t = parseTiming(value, `@keyframes ${name}`, warn);
                if (t) frame.timing = t;
            } else warn(`@keyframes ${name}: CSS "${prop}" is not supported (transform and opacity are); ignored.`);
        }
        for (const offset of offsets) frames.push({ ...frame, offset });
    }
    frames.sort((a, b) => a.offset - b.offset);
    return { name, frames };
}

// ── Animation / transition declarations ─────────────────

const DIRECTIONS = ['normal', 'reverse', 'alternate', 'alternate-reverse'];
const FILLS = ['none', 'forwards', 'backwards', 'both'];

/** The `animation` declarations of one element as specs (one per comma-separated animation). */
export function parseAnimations(decls: Record<string, string>, where: string, warn: Warn): AnimationSpec[] {
    const list = decls.animation !== undefined ? splitTop(decls.animation, /,/) : [];
    const specs: AnimationSpec[] = list.map(item => {
        const spec: AnimationSpec = { name: '', duration: 0, delay: 0, timing: EASE, iterations: 1, direction: 'normal', fill: 'none' };
        let times = 0;
        for (const token of splitTop(item, /\s/)) {
            const t = seconds(token);
            if (t !== null) { if (times === 0) spec.duration = t; else if (times === 1) spec.delay = t; times++; continue; }
            const timing = parseTiming(token, where, warn);
            if (timing) { spec.timing = timing; continue; }
            if (token === 'infinite') { spec.iterations = Infinity; continue; }
            if (/^\d*\.?\d+$/.test(token)) { spec.iterations = parseFloat(token); continue; }
            if (DIRECTIONS.includes(token)) { spec.direction = token as AnimationSpec['direction']; continue; }
            if (FILLS.includes(token)) { spec.fill = token as AnimationSpec['fill']; continue; }
            if (token === 'running') continue;
            if (token === 'paused') { warn(`${where}: animation-play-state: paused is not supported; played.`); continue; }
            if (token === 'none') continue;
            spec.name = token;
        }
        return spec;
    });
    if (!specs.length && decls['animation-name'] !== undefined) specs.push({ name: '', duration: 0, delay: 0, timing: EASE, iterations: 1, direction: 'normal', fill: 'none' });
    // Longhands override the shorthand and apply to the first animation.
    const first = specs[0];
    if (first) {
        const name = decls['animation-name'];
        if (name !== undefined) first.name = splitTop(name, /,/)[0] === 'none' ? '' : splitTop(name, /,/)[0];
        const dur = decls['animation-duration'] !== undefined ? seconds(splitTop(decls['animation-duration'], /,/)[0]) : null;
        if (dur !== null) first.duration = dur;
        const delay = decls['animation-delay'] !== undefined ? seconds(splitTop(decls['animation-delay'], /,/)[0]) : null;
        if (delay !== null) first.delay = delay;
        const tf = decls['animation-timing-function'] !== undefined ? parseTiming(splitTop(decls['animation-timing-function'], /,/)[0], where, warn) : null;
        if (tf) first.timing = tf;
        const count = decls['animation-iteration-count']?.split(',')[0].trim();
        if (count !== undefined) first.iterations = count === 'infinite' ? Infinity : Number.isFinite(parseFloat(count)) ? parseFloat(count) : first.iterations;
        const dir = decls['animation-direction']?.split(',')[0].trim();
        if (dir && DIRECTIONS.includes(dir)) first.direction = dir as AnimationSpec['direction'];
        const fill = decls['animation-fill-mode']?.split(',')[0].trim();
        if (fill && FILLS.includes(fill)) first.fill = fill as AnimationSpec['fill'];
        if (decls['animation-play-state']?.includes('paused')) warn(`${where}: animation-play-state: paused is not supported; played.`);
    }
    return specs.filter(s => {
        if (!s.name) return false;
        if (!(s.duration > 0)) { warn(`${where}: animation "${s.name}" has no duration; skipped.`); return false; }
        if (!(s.iterations > 0)) { warn(`${where}: animation "${s.name}" has an iteration count that is not above 0; skipped.`); return false; }
        return true;
    });
}

/** The `transition` declarations of one element: transform and/or opacity (or `all`). */
export function parseTransition(decls: Record<string, string>, where: string, warn: Warn): TransitionSpec | undefined {
    const items = decls.transition !== undefined ? splitTop(decls.transition, /,/) : [];
    const longProps = decls['transition-property'] !== undefined ? splitTop(decls['transition-property'], /,/) : [];
    const longDuration = decls['transition-duration'] !== undefined ? seconds(splitTop(decls['transition-duration'], /,/)[0]) : null;
    const longDelay = decls['transition-delay'] !== undefined ? seconds(splitTop(decls['transition-delay'], /,/)[0]) : null;
    const longTiming = decls['transition-timing-function'] !== undefined ? parseTiming(splitTop(decls['transition-timing-function'], /,/)[0], where, warn) : null;
    const entries: { prop: string; duration: number; delay: number; timing: Timing }[] = items.map(item => {
        const e = { prop: 'all', duration: 0, delay: 0, timing: EASE };
        let times = 0;
        for (const token of splitTop(item, /\s/)) {
            const t = seconds(token);
            if (t !== null) { if (times === 0) e.duration = t; else e.delay = t; times++; continue; }
            const timing = parseTiming(token, where, warn);
            if (timing) { e.timing = timing; continue; }
            e.prop = token;
        }
        return e;
    });
    if (!entries.length && longProps.length) entries.push(...longProps.map(prop => ({ prop, duration: 0, delay: 0, timing: EASE })));
    if (!entries.length) return undefined;
    for (const e of entries) {
        if (longDuration !== null && decls.transition === undefined) e.duration = longDuration;
        if (longDelay !== null && decls.transition === undefined) e.delay = longDelay;
        if (longTiming && decls.transition === undefined) e.timing = longTiming;
    }
    const used = entries.filter(e => {
        if (e.prop === 'all' || e.prop === 'transform' || e.prop === 'opacity') return true;
        if (e.prop !== 'none') warn(`${where}: transition of "${e.prop}" is not supported (transform and opacity are); ignored.`);
        return false;
    });
    if (!used.length) return undefined;
    const first = used[0];
    if (used.some(e => e.duration !== first.duration || e.delay !== first.delay || JSON.stringify(e.timing) !== JSON.stringify(first.timing))) {
        warn(`${where}: transform and opacity transitions differ in timing; the first one's is used for both.`);
    }
    const props = new Set<'transform' | 'opacity'>();
    for (const e of used) { if (e.prop === 'all') { props.add('transform'); props.add('opacity'); } else props.add(e.prop as 'transform' | 'opacity'); }
    return { properties: [...props], duration: first.duration, delay: first.delay, timing: first.timing };
}

// ── Sampling ────────────────────────────────────────────

/** Frames with the implicit 0% and 100% (the element's own pose) filled in. */
function closed(frames: Frame[], key: 'pose' | 'opacity', rest: Pose | number): Frame[] {
    const own = frames.filter(f => f[key] !== undefined);
    if (!own.length) return [];
    const out = own.map(f => ({ ...f }));
    if (out[0].offset > 0) out.unshift({ offset: 0, [key]: rest });
    if (out[out.length - 1].offset < 1) out.push({ offset: 1, [key]: rest });
    return out;
}

const lerp = (a: number, b: number, u: number) => a + (b - a) * u;
function mixPose(a: Pose, b: Pose, u: number): Pose {
    return {
        t: a.t.map((l, k) => ({ px: lerp(l.px, b.t[k].px, u), pct: lerp(l.pct, b.t[k].pct, u) })) as Pose['t'],
        r: a.r.map((v, k) => lerp(v, b.r[k], u)) as Pose['r'],
        s: a.s.map((v, k) => lerp(v, b.s[k], u)) as Pose['s'],
    };
}

/** Value of a closed frame list at progress `p` (0..1) of one forward pass, easing each segment with its start frame's timing or `fallback`. */
function atProgress<T>(frames: Frame[], key: 'pose' | 'opacity', p: number, fallback: Timing, mix: (a: T, b: T, u: number) => T): T {
    let i = 0;
    while (i < frames.length - 2 && p >= frames[i + 1].offset) i++;
    const a = frames[i];
    const b = frames[i + 1];
    const span = b.offset - a.offset;
    const u = span <= 0 ? 1 : Math.min(1, Math.max(0, (p - a.offset) / span));
    return mix(a[key] as T, b[key] as T, applyTiming(a.timing ?? fallback, u));
}

/** The progress through the keyframes (0..1) at time `tau` into the animation, after direction and iteration. */
function progressAt(spec: AnimationSpec, tau: number): number {
    const d = spec.duration;
    const total = spec.iterations * d;
    const clamped = Math.min(Math.max(tau, 0), total);
    let k = Math.floor(clamped / d);
    let u = (clamped - k * d) / d;
    if (clamped >= total && Number.isFinite(total)) { k = Math.ceil(spec.iterations) - 1; u = spec.iterations - k; }
    const odd = k % 2 === 1;
    const reversed = spec.direction === 'reverse' || (spec.direction === 'alternate' && odd) || (spec.direction === 'alternate-reverse' && !odd);
    return reversed ? 1 - u : u;
}

/** Seconds one cycle of a looping animation takes (an alternating one goes out and back). */
export const cycleOf = (spec: AnimationSpec) => spec.direction === 'alternate' || spec.direction === 'alternate-reverse' ? spec.duration * 2 : spec.duration;

const MAX_KEYS = 3601;

export interface ClipPlan {
    /** The keyframes of the clip, a pose per key as offsets from the block's own pose. */
    keys: TransformKeyframe[];
    fps: number;
    /** Plays once as the `enter` clip (`delay` seconds before it starts), or loops as `main`. */
    loop: boolean;
    /** Seconds the engine holds the first pose before starting (backwards fill, or any delay of an entrance). */
    hold: number;
    /** A loop whose last pose is not its first. */
    seam: boolean;
}

/** Quaternion [x, y, z, w] of the CSS rotations (about x, y, z in that order) in the scene's axes. */
export function rotationQuaternion(r: readonly [number, number, number]): [number, number, number, number] {
    const rad = Math.PI / 180;
    // CSS to scene: x about +x is reversed (the top tips away along -z), z is reversed (clockwise); y is the same.
    const axis = (x: number, y: number, z: number, deg: number): [number, number, number, number] => {
        const h = deg * rad / 2;
        const s = Math.sin(h);
        return [x * s, y * s, z * s, Math.cos(h)];
    };
    const mul = (a: number[], b: number[]): [number, number, number, number] => [
        a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
        a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
        a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
        a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
    ];
    return mul(mul(axis(1, 0, 0, -r[0]), axis(0, 1, 0, r[1])), axis(0, 0, 1, -r[2]));
}

export interface Metrics {
    /** Metres per CSS px. */
    metresPerPx: number;
    /** The block's width in metres, for `%` lengths. */
    width: number;
}

/** A pose in the scene: position in metres, rotation quaternion, scale. */
export function bakePose(pose: Pose, m: Metrics): { position: [number, number, number]; rotation: [number, number, number, number]; scale: [number, number, number] } {
    const len = (l: Len) => l.px * m.metresPerPx + l.pct / 100 * m.width;
    return {
        position: [len(pose.t[0]), -len(pose.t[1]), len(pose.t[2])],
        rotation: rotationQuaternion(pose.r),
        scale: [...pose.s],
    };
}

const round = (v: number) => Math.round(v * 1e6) / 1e6 + 0;
const sameSign = (q: number[], prev: number[] | undefined) => (prev && q[0] * prev[0] + q[1] * prev[1] + q[2] * prev[2] + q[3] * prev[3] < 0 ? q.map(v => -v) : q) as [number, number, number, number];

/**
 * The clip for a transform animation. A loop is one cycle, played as `main`; anything finite is the delay (when the element shows its own
 * pose through it), every iteration, and the pose it ends on, played once as `enter`.
 */
export function planClip(spec: AnimationSpec, frames: Frame[], m: Metrics): ClipPlan | null {
    const closedFrames = closed(frames, 'pose', REST);
    if (!closedFrames.length) return null;
    const loop = !Number.isFinite(spec.iterations);
    const timeline = loop ? cycleOf(spec) : spec.duration * spec.iterations;
    let fps = 30;
    if (timeline * fps + 1 > MAX_KEYS) fps = Math.max(1, Math.floor((MAX_KEYS - 1) / timeline));
    const backwards = spec.fill === 'backwards' || spec.fill === 'both';
    const forwards = spec.fill === 'forwards' || spec.fill === 'both';
    // A delay the element shows its own pose through is part of the clip; one it waits posed for is the engine's hold.
    const lead = !loop && spec.delay > 0 && !backwards ? spec.delay : 0;
    const times = new Set<number>();
    for (let k = 0; k <= timeline * fps + 1e-9; k++) times.add(round(k / fps));
    times.add(round(timeline));
    if (spec.iterations <= 64) {
        for (let it = 0; it < Math.ceil(Math.min(spec.iterations, loop ? 2 : Infinity)); it++) {
            for (const f of closedFrames) { const at = it * spec.duration + f.offset * spec.duration; if (at <= timeline) times.add(round(at)); }
        }
    }
    // Where one iteration ends and the next starts over (not an alternating run, which is continuous), the end pose is a key just before.
    if (!loop && spec.direction !== 'alternate' && spec.direction !== 'alternate-reverse') {
        for (let it = 1; it < Math.ceil(spec.iterations); it++) times.add(round(it * spec.duration - 0.001));
    }
    const sorted = [...times].filter(t => t <= timeline + 1e-9).sort((a, b) => a - b);
    // The end of a looping cycle is sampled just inside it: at its exact end the next cycle has begun.
    const sample = (tau: number) => bakePose(atProgress<Pose>(closedFrames, 'pose', progressAt(spec, loop ? Math.min(tau, timeline - 1e-9) : tau), spec.timing, mixPose), m);
    const keys: TransformKeyframe[] = [];
    let prev: number[] | undefined;
    const push = (time: number, pose: ReturnType<typeof bakePose>) => {
        const rotation = sameSign(pose.rotation, prev);
        prev = rotation;
        keys.push({ time: round(time), position: pose.position.map(round) as [number, number, number], rotation: rotation.map(round) as [number, number, number, number], scale: pose.scale.map(round) as [number, number, number] });
    };
    const restBaked = bakePose(REST, m);
    if (lead > 0) { push(0, restBaked); push(lead - 1 / fps / 2, restBaked); }
    for (const tau of sorted) push(lead + tau, sample(tau));
    if (!loop && !forwards) push(lead + timeline + 1 / fps, restBaked);
    const first = keys[0];
    const last = keys[loop ? keys.length - 1 : 0];
    const seam = loop && (Math.max(...first.position!.map((v, i) => Math.abs(v - last.position![i]))) > 0.001
        || Math.max(...first.scale!.map((v, i) => Math.abs(v - last.scale![i]))) > 0.001
        || Math.abs(first.rotation!.reduce((s, v, i) => s + v * last.rotation![i], 0)) < Math.cos(Math.PI / 180 / 2));
    return { keys, fps, loop, hold: loop ? Math.max(0, spec.delay) : backwards ? Math.max(0, spec.delay) : 0, seam };
}

/** Piecewise expression of `phase` (0..1 over one cycle) for an opacity animation. */
export function opacityCycle(spec: AnimationSpec, frames: Frame[], phase: string): { expression: string; start: number } | null {
    const base = closed(frames, 'opacity', 1);
    if (!base.length) return null;
    const alternate = spec.direction === 'alternate' || spec.direction === 'alternate-reverse';
    const forward = spec.direction === 'normal' || spec.direction === 'alternate';
    const pass = forward ? base : base.map(f => ({ ...f, offset: 1 - f.offset })).reverse();
    // One cycle as (position, value) stops, each segment keeping the timing of the keyframe it starts at.
    const stops: { at: number; value: number; timing: Timing }[] = pass.map(f => ({ at: f.offset, value: f.opacity as number, timing: f.timing ?? spec.timing }));
    const cycle = alternate ? [...stops.map(s => ({ ...s, at: s.at / 2 })), ...[...stops].reverse().slice(1).map(s => ({ ...s, at: 1 - s.at / 2 }))] : stops;
    const num = (v: number) => String(round(v));
    const segments = cycle.slice(0, -1).map((s, i) => {
        const next = cycle[i + 1];
        const eased = s.timing.kind === 'bezier';
        const u = eased ? `smoothstep(${num(s.at)}, ${num(next.at)}, ${phase})` : `((${phase} - ${num(s.at)}) / ${num(next.at - s.at)})`;
        return { to: next.at, value: next.at - s.at <= 0 ? num(next.value) : `(${num(s.value)} + ${num(next.value - s.value)} * ${u})` };
    });
    let expression = num(cycle[cycle.length - 1].value);
    for (let i = segments.length - 1; i >= 0; i--) expression = `${phase} < ${num(segments[i].to)} ? ${segments[i].value} : ${expression}`;
    return { expression: `{${expression}}`, start: cycle[0].value };
}

/** Wait before the first pass as a starting phase of a clock (0..1): a negative delay starts part-way in. */
export function startPhase(spec: AnimationSpec): number {
    const cycle = cycleOf(spec);
    const p = (-spec.delay / cycle) % 1;
    return round(p < 0 ? p + 1 : p);
}

/** The first and last opacity of a finished animation and the transition that takes it there. */
export function opacityRun(spec: AnimationSpec, frames: Frame[]): { from: number; to: number; stops: number } | null {
    const base = closed(frames, 'opacity', 1);
    if (!base.length) return null;
    const reversed = spec.direction === 'reverse' || spec.direction === 'alternate-reverse';
    // The last pass runs backwards when the first does, flipped once more by an even number of alternating passes.
    const even = Math.ceil(spec.iterations) % 2 === 0;
    const lastReversed = reversed !== (spec.direction.startsWith('alternate') && even);
    const a = base[0].opacity as number;
    const b = base[base.length - 1].opacity as number;
    return { from: reversed ? b : a, to: lastReversed ? a : b, stops: base.length };
}

/** Which CSS declarations apply to an element: stylesheet rules by weight and order, then the inline style. */
export function cascade(sheet: Sheet, tag: string, classes: string[], id: string | undefined, pseudo: Rule['pseudo'], inline: Record<string, string>): Record<string, string> {
    const matching = sheet.rules.filter(r => r.pseudo === pseudo && (!r.tag || r.tag === tag) && r.classes.every(c => classes.includes(c)) && (!r.id || r.id === id))
        .sort((a, b) => a.weight - b.weight || a.order - b.order);
    return Object.assign({}, ...matching.map(r => r.decls), pseudo === '' ? inline : {});
}
